import { useCallback, useEffect, useRef, useState } from "react";

interface UseIntersectionObserverProps {
  callback: () => void; // The function to call when intersection is triggered
  cursor?: string | null; // Cursor to track changes for pagination
  rootMargin?: string; // Optional margin around root
  threshold?: number; // Optional threshold for triggering
  root?: HTMLElement | null;
  isFetching?: boolean; // Skip triggering while a fetch is already in flight
}

export const useIntersectionObserver = ({
  callback,
  cursor,
  rootMargin = "0px",
  threshold = 0.1,
  root = null,
  isFetching = false,
}: UseIntersectionObserverProps) => {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const observerRef = useCallback((node: HTMLElement | null) => {
    setTarget(node);
  }, []);

  const callbackRef = useRef(callback);
  const cursorRef = useRef(cursor);
  const isFetchingRef = useRef(isFetching);
  const observerInstanceRef = useRef<IntersectionObserver | null>(null);

  callbackRef.current = callback;
  cursorRef.current = cursor;
  isFetchingRef.current = isFetching;

  useEffect(() => {
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry!.isIntersecting && cursorRef.current && !isFetchingRef.current) {
          callbackRef.current();
        }
      },
      { root, rootMargin, threshold }
    );
    observerInstanceRef.current = observer;

    observer.observe(target);

    // Cleanup observer when the sentinel unmounts or the root/options change.
    return () => {
      observer.disconnect();
      observerInstanceRef.current = null;
    };
  }, [target, rootMargin, threshold, root]);

  const wasFetchingRef = useRef(isFetching);
  useEffect(() => {
    const justFinished = wasFetchingRef.current && !isFetching;
    wasFetchingRef.current = isFetching;

    if (justFinished && target && observerInstanceRef.current) {
      observerInstanceRef.current.unobserve(target);
      observerInstanceRef.current.observe(target);
    }
  }, [isFetching, target]);

  return observerRef;
};
